import { Service } from '@toeverything/infra';
import * as Sentry from '@sentry/react';

import type {
  APITiming,
  PageContext,
  PageLoadStages,
  PerformanceConfig,
  PerformanceMetrics,
} from '../types';

let globalInstance: PerformanceMonitorService | null = null;

export class PerformanceMonitorService extends Service {
  private readonly marks = new Map<string, number>();
  private readonly measures = new Map<string, number>();
  private metrics: Partial<PerformanceMetrics> = {};
  private apiTimings: APITiming[] = [];
  private pageLoadStages: PageLoadStages = {};
  private hasReported = false;

  constructor() {
    super();
    if (globalInstance) {
      return globalInstance as any;
    }
    globalInstance = this;
  }

  private readonly config: PerformanceConfig = {
    enabled: true,
    enableAPIMonitoring: true,
    sampleRate: BUILD_CONFIG.debug ? 1 : 0.9,
    maxAPITimings: 50,
  };

  private shouldCollect(): boolean {
    if (!this.config.enabled) return false;
    return Math.random() < this.config.sampleRate;
  }

  setPageContext(context: PageContext) {
    this.metrics.pageContext = context;
    this.pageLoadStages = {};
    this.metrics.pageLoadStages = {};
    this.hasReported = false;
  }

  mark(name: string, metadata?: Record<string, any>) {
    if (!this.config.enabled) return;

    const time = performance.now();
    this.marks.set(name, time);

    try {
      performance.mark(name, { detail: metadata });
    } catch (e) {
      // 静默失败
    }
  }

  measure(name: string, startMark: string, endMark?: string): number {
    if (!this.config.enabled) return 0;

    const start = this.marks.get(startMark);
    const end = endMark ? this.marks.get(endMark) : performance.now();

    if (!start) {
      return 0;
    }

    const duration = (end || performance.now()) - start;
    this.measures.set(name, duration);

    try {
      if (endMark) {
        performance.measure(name, startMark, endMark);
      } else {
        performance.measure(name, startMark);
      }
    } catch (e) {
      // 静默失败
    }

    return duration;
  }

  setPageLoadStage(stage: keyof PageLoadStages, duration: number) {
    this.pageLoadStages[stage] = duration;
    this.metrics.pageLoadStages = { ...this.pageLoadStages };
  }

  setSharedWorkerMetrics(metrics: {
    startTime: number;
    connectionTime: number;
    firstMessageTime: number;
    totalTime: number;
  }) {
    if (!this.metrics.appInitialization) {
      this.metrics.appInitialization = {};
    }
    this.metrics.appInitialization.sharedWorkerInit = metrics;
  }

  setWebSocketMetrics(metrics: {
    connectionStart: number;
    connectionEstablished: number;
    firstDataReceived: number;
    authTime?: number;
  }) {
    if (!this.metrics.dataLoading) {
      this.metrics.dataLoading = {};
    }
    this.metrics.dataLoading.websocket = metrics;
  }

  setIndexedDBMetrics(metrics: { openTime: number; firstQuery: number }) {
    if (!this.metrics.dataLoading) {
      this.metrics.dataLoading = {};
    }
    this.metrics.dataLoading.indexedDB = metrics;
  }

  recordAPITiming(timing: APITiming) {
    if (!this.config.enableAPIMonitoring || this.hasReported) return;

    this.apiTimings.push(timing);

    if (this.apiTimings.length > this.config.maxAPITimings) {
      this.apiTimings.shift();
    }
  }

  private collectAPITimings() {
    if (!this.config.enableAPIMonitoring || this.apiTimings.length === 0) {
      return;
    }

    const totalDuration = this.apiTimings.reduce(
      (sum, t) => sum + t.duration,
      0
    );
    const slowest = this.apiTimings.reduce(
      (max, t) => (t.duration > (max?.duration || 0) ? t : max),
      null as APITiming | null
    );

    this.metrics.apiTimings = {
      summary: {
        totalRequests: this.apiTimings.length,
        averageDuration: totalDuration / this.apiTimings.length,
        slowest,
      },
    };
  }

  async collectWebVitals(): Promise<void> {
    if (!this.config.enabled) return;

    try {
      const webVitals = await import('web-vitals');

      this.metrics.overall = this.metrics.overall || {};

      webVitals.onFCP((metric: any) => {
        if (this.metrics.overall) {
          this.metrics.overall.FCP = metric.value;
        }
      });

      webVitals.onLCP((metric: any) => {
        if (this.metrics.overall) {
          this.metrics.overall.LCP = metric.value;
        }
      });

      webVitals.onCLS((metric: any) => {
        if (this.metrics.overall) {
          this.metrics.overall.CLS = metric.value;
        }
      });

      webVitals.onTTFB((metric: any) => {
        if (this.metrics.overall) {
          this.metrics.overall.TTFB = metric.value;
        }
      });
    } catch (e) {
      // 静默失败
    }
  }

  async report() {
    if (!this.config.enabled || !this.shouldCollect()) return;
    if (this.hasReported) return;

    this.hasReported = true;

    const reportAsync = () => {
      this.collectAPITimings();

      const completeMetrics: Partial<PerformanceMetrics> = {
        ...this.metrics,
        customMarks: Object.fromEntries(this.marks),
        customMeasures: Object.fromEntries(this.measures),
      };

      this.reportToSentry(completeMetrics);
      this.reportToTrack(completeMetrics);

      // 上报后清理，释放内存
      this.marks.clear();
      this.measures.clear();
      this.apiTimings = [];
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(reportAsync, { timeout: 3000 });
    } else {
      setTimeout(reportAsync, 200);
    }
  }

  private reportToSentry(metrics: Partial<PerformanceMetrics>) {
    if (!BUILD_CONFIG.SENTRY_DSN) return;

    try {
      Sentry.setContext('performance', {
        pageType: metrics.pageContext?.pageType,
        userRole: metrics.pageContext?.userRole,
        sharedWorkerInit:
          metrics.appInitialization?.sharedWorkerInit?.totalTime,
        websocketConnection:
          metrics.dataLoading?.websocket?.connectionEstablished,
        apiAverageDuration: metrics.apiTimings?.summary?.averageDuration,
        apiTotalRequests: metrics.apiTimings?.summary?.totalRequests,
        fcp: metrics.overall?.FCP,
        lcp: metrics.overall?.LCP,
        cls: metrics.overall?.CLS,
        ttfb: metrics.overall?.TTFB,
        ...metrics.pageLoadStages,
      });

      Sentry.captureMessage('Page Load Performance', {
        level: 'info',
        tags: {
          type: 'performance',
          pageType: metrics.pageContext?.pageType || 'unknown',
        },
      });
    } catch (e) {
      // 静默失败
    }
  }

  private reportToTrack(metrics: Partial<PerformanceMetrics>) {
    import('@affine/track')
      .then(({ aliTrack }) => {
        aliTrack('page_load_metrics', {
          page_type: metrics.pageContext?.pageType,
          workspace_id: metrics.pageContext?.workspaceId,
          doc_id: metrics.pageContext?.docId,
          user_role: metrics.pageContext?.userRole,
          can_edit: metrics.pageContext?.canEdit,
          is_in_trash: metrics.pageContext?.isInTrash,

          fcp: metrics.overall?.FCP,
          lcp: metrics.overall?.LCP,
          cls: metrics.overall?.CLS,
          ttfb: metrics.overall?.TTFB,

          shared_worker_init:
            metrics.appInitialization?.sharedWorkerInit?.totalTime,
          websocket_connection:
            metrics.dataLoading?.websocket?.connectionEstablished,
          indexeddb_open: metrics.dataLoading?.indexedDB?.openTime,

          api_requests: metrics.apiTimings?.summary?.totalRequests,
          api_avg_duration: metrics.apiTimings?.summary?.averageDuration,
          api_slowest_duration: metrics.apiTimings?.summary?.slowest?.duration,

          doc_list_ready_time: metrics.pageLoadStages?.docListReady,
          doc_opened_time: metrics.pageLoadStages?.docOpened,
          editor_created_time: metrics.pageLoadStages?.editorCreated,
          data_ready_time: metrics.pageLoadStages?.dataReady,
          render_complete_time: metrics.pageLoadStages?.renderComplete,

          environment: BUILD_CONFIG.debug ? 'dev' : 'prod',
        });
      })
      .catch(() => {});
  }

  override dispose(): void {
    this.marks.clear();
    this.measures.clear();
    this.apiTimings = [];
    this.metrics = {};
    this.hasReported = false;
    super.dispose();
  }
}
